import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { StepList } from '../components/StepList';
import { CTABand } from '../components/CTABand';
import { ContactCTA } from '../components/ContactCTA';
import { useSeo } from '../hooks/useSeo';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CandidatForm } from '../components/CandidatForm';
import { ShieldCheck, Briefcase, MessageSquare, FileText, Users, Target, Info, PenTool, ArrowDown } from 'lucide-react';
const CANDIDAT_STEPS = [{
  title: 'Premier contact',
  description: 'Remplissez le formulaire de candidature en ligne pour nous faire part de votre projet.',
  icon: <MessageSquare className="w-6 h-6" data-id="element-887" data-mp="3hvc59pltrh59" />
}, {
  title: 'Dossier candidat',
  description: 'Transmission de votre CV, lettre de motivation et bulletins scolaires.',
  icon: <FileText className="w-6 h-6" data-id="element-888" data-mp="3pmuxh04q4r0b" />
}, {
  title: "Entretien d'admission",
  description: 'Échange avec notre équipe pour évaluer votre motivation et la cohérence de votre projet.',
  icon: <Users className="w-6 h-6" data-id="element-889" data-mp="37a7mxutmnbl" />
}, {
  title: 'Positionnement',
  description: 'Validation de votre profil et de vos prérequis pour la formation visée.',
  icon: <Target className="w-6 h-6" data-id="element-890" data-mp="c6dqc0q2t0pk" />
}, {
  title: 'Information complète',
  description: 'Présentation détaillée du rythme, du programme et des attentes du CFA.',
  icon: <Info className="w-6 h-6" data-id="element-891" data-mp="m52yv2w318t5" />
}, {
  title: "Contrat d'alternance",
  description: "Signature du contrat avec votre entreprise d'accueil et inscription définitive.",
  icon: <PenTool className="w-6 h-6" data-id="element-892" data-mp="2nwpw3m6khzy2" />
}];
export function Candidater() {
  useSeo({
    title: 'Candidater en alternance — Rentrée le 25 septembre 2026',
    description: "Déposez votre candidature en alternance chez Zillenium Alternance (Joigny, 89). Offres disponibles avec démarrage immédiat possible en entreprise, rentrée en formation le 25 septembre 2026. Admission sans frais.",
    path: '/candidater'
  });
  return <div className="flex flex-col w-full" data-id="element-893" data-mp="3hlf5nedzg3tr">
      <PageHeader title="Candidater chez Zillenium" subtitle="Rejoignez notre CFA et donnez un coup d'accélérateur à votre carrière grâce à l'alternance." data-id="element-894" data-mp="3hojewq6b1pra" />

      <div className="py-16 md:py-24 bg-muted relative overflow-hidden" data-id="element-895" data-mp="3rk4n561yvm9h">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-896" data-mp="3vl6gj697d43j" />
        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-897" data-mp="49n67rh3e2jo">
          <div className="text-center mb-20 max-w-3xl mx-auto" data-id="element-898" data-mp="95byd9qs8h5y">
            <p className="text-xl md:text-2xl text-primary-dark/80 leading-relaxed font-medium" data-id="element-899" data-mp="e5zw0e9inw95">
              Que vous soyez lycéen, étudiant, en reconversion ou parent
              accompagnant un jeune dans son orientation, notre équipe est là
              pour vous guider à chaque étape de votre admission.
            </p>
            <div className="mt-10" data-id="element-900" data-mp="1ymr3yuo7q1fx">
              <Button size="lg" className="group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" onClick={() => document.getElementById('candidat-form')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-901" data-mp="1ypvdm7kg5pdz">
                Remplir le formulaire de candidature
                <ArrowDown className="w-5 h-5 ml-2 group-hover:translate-y-1 transition-transform" data-id="element-902" data-mp="2cnjyhfeikwj1" />
              </Button>
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-16 items-start mb-24" data-id="element-903" data-mp="27np3aznphks6">
            <div className="lg:col-span-3" data-id="element-904" data-mp="3544cey0881s6">
              <SectionHeading title="Le processus d'admission" className="mb-12" data-id="element-905" data-mp="355xmkna1t44x" />
              <StepList steps={CANDIDAT_STEPS} data-id="element-906" data-mp="2kveb3qu7xcy7" />
            </div>

            <div className="lg:col-span-2 space-y-8 sticky top-8" data-id="element-907" data-mp="2kttja8zag841">
              <Card className="bg-white border-none shadow-xl hover:-translate-y-2 transition-all duration-300 group" data-id="element-908" data-mp="2pv8z9mmlzofk">
                <div className="h-2 w-full bg-brand-orange" data-id="element-909" data-mp="2ayi2zm88esu6" />
                <CardContent className="p-8" data-id="element-910" data-mp="2fv8f5ygx14ab">
                  <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-911" data-mp="2fwa0cvrnb75r">
                    <Briefcase className="w-8 h-8 text-brand-orange animate-soft-bounce" data-id="element-912" data-mp="38jnteis83ikl" />
                  </div>
                  <h3 className="font-extrabold font-heading text-2xl text-primary-dark mb-4" data-id="element-913" data-mp="2tv1ak73e08om">
                    Aide à la recherche d'entreprise
                  </h3>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-6" data-id="element-914" data-mp="3mk8b1bmej8ci">
                    Une fois votre candidature validée, Zillenium Alternance
                    vous accompagne activement dans votre recherche d'employeur
                    (ateliers CV, préparation aux entretiens, mise en relation
                    avec notre réseau de partenaires).
                  </p>
                  <Button variant="outline" className="w-full border-primary/20 text-primary-dark hover:bg-primary/5 font-semibold" onClick={() => document.getElementById('cap-360-section')?.scrollIntoView({
                  behavior: 'smooth'
                })} data-id="element-915" data-mp="1pmapni97vtpy">
                    En savoir plus sur l'accompagnement CAP 360
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-primary-dark text-white border-none shadow-xl hover:-translate-y-2 transition-all duration-300 group" data-id="element-916" data-mp="2hcntjrnlukbc">
                <div className="h-2 w-full bg-primary-accent" data-id="element-917" data-mp="2db299l8qr3r4" />
                <CardContent className="p-8" data-id="element-918" data-mp="1a1hg1kx17hik">
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-919" data-mp="1a0g135cwaavk">
                    <ShieldCheck className="w-8 h-8 text-lavande animate-soft-pulse" data-id="element-920" data-mp="1jzxanxv9p8wt" />
                  </div>
                  <h3 className="font-extrabold font-heading text-2xl mb-4" data-id="element-921" data-mp="1nz6411meb4ru">
                    Confidentialité
                  </h3>
                  <p className="text-lg text-lavande leading-relaxed" data-id="element-922" data-mp="r8dihogp0kio">
                    Les informations recueillies via notre formulaire sont
                    strictement confidentielles et traitées dans le respect du
                    RGPD, uniquement dans le cadre de votre candidature.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* CAP 360 Section */}
      <section id="cap-360-section" className="py-24 bg-white relative overflow-hidden scroll-mt-24" data-id="element-923" data-mp="cjzjjomedg7e">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-924" data-mp="50zzdgr43kmz" />
        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-925" data-mp="3s5zepigdckbe">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-926" data-mp="3s8bupq2kgklt">
            <div data-id="element-927" data-mp="3n6w8vvskea2d">
              <SectionHeading title="CAP 360" subtitle="Un accompagnement vers l'alternance, avant l'entrée en formation" className="mb-8" data-id="element-928" data-mp="3n9ifnt7msljj" />
              <p className="text-lg text-muted-foreground leading-relaxed mb-8" data-id="element-929" data-mp="3dfi4b9lrak2d">
                CAP 360 est un dispositif d'accompagnement mis en place par
                Zillenium Alternance pour aider les futurs apprenants à se
                préparer concrètement à la recherche d'une alternance, avant le
                démarrage des cours. À travers des bootcamps, des ateliers TRE,
                des temps de remise en confiance et un accompagnement
                personnalisé, nous aidons les candidats à lever les freins,
                clarifier leur projet et adopter une posture professionnelle
                adaptée aux attentes des recruteurs.
              </p>

              <div className="bg-muted p-8 rounded-3xl mb-8" data-id="element-930" data-mp="d92srv627qim">
                <h4 className="font-bold text-primary-dark mb-4" data-id="element-931" data-mp="i7ntdvp84c7l">
                  Les futurs apprenants bénéficient notamment de :
                </h4>
                <ul className="space-y-3" data-id="element-932" data-mp="3hq0x3k9h0cl">
                  <li className="flex items-start gap-3" data-id="element-933" data-mp="8aj2xjochukz">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2 shrink-0" data-id="element-934" data-mp="8c42jnpio3pp"></div>
                    <span className="text-muted-foreground" data-id="element-935" data-mp="3qmfll4dj34oz">
                      Ateliers de refonte et d'optimisation des CV
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-936" data-mp="3gquqzrash6p9">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2 shrink-0" data-id="element-937" data-mp="3lpffwuklc3ye"></div>
                    <span className="text-muted-foreground" data-id="element-938" data-mp="322uer0546bfm">
                      Préparation aux entretiens et simulations en conditions
                      réelles
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-939" data-mp="3c2l7oxe10idl">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2 shrink-0" data-id="element-940" data-mp="372gsdaouttoy"></div>
                    <span className="text-muted-foreground" data-id="element-941" data-mp="374tf015k4s8c">
                      Travail sur la confiance en soi, l'expression orale et la
                      posture professionnelle
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-942" data-mp="3usb39mpm586k">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2 shrink-0" data-id="element-943" data-mp="3utvwn906yl2u"></div>
                    <span className="text-muted-foreground" data-id="element-944" data-mp="3pvavn7d47xdc">
                      Accompagnement pour dépasser les freins psychologiques
                      liés à la recherche d'emploi
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-945" data-mp="2p71tnix1mvuq">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2 shrink-0" data-id="element-946" data-mp="2p8lapbqixaxk"></div>
                    <span className="text-muted-foreground" data-id="element-947" data-mp="2kdyzb8kfzidh">
                      Interventions de professionnels et de partenaires du
                      terrain
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-primary-dark font-bold leading-relaxed mb-8" data-id="element-948" data-mp="384l5euavysku">
                CAP 360 repose sur un travail commun entre l'étudiant et
                l'équipe Zillenium Alternance. L'objectif n'est pas de faire à
                la place, mais de donner les outils, la méthode et le cadre pour
                permettre à chacun de s'engager efficacement dans sa recherche
                d'alternance.
              </p>
              <Button size="lg" className="group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-8 py-6 shadow-xl" onClick={() => document.getElementById('candidat-form')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-949" data-mp="1mq71ccdk8d4u">
                Remplir le formulaire de candidature
                <ArrowDown className="w-5 h-5 ml-2 group-hover:translate-y-1 transition-transform" data-id="element-950" data-mp="c025hg7dggc0" />
              </Button>
            </div>

            {/* Vidéo de présentation */}
            <div className="lg:sticky lg:top-28" data-id="element-951" data-mp="h19efil93lk9">
              <div className="w-full max-w-sm mx-auto" data-id="element-952" data-mp="lxgkdbdncx27">
                <div className="bg-white p-3 rounded-[2rem] shadow-2xl border border-slate-100" data-id="element-953" data-mp="m02v1jvu3son">
                  <div className="relative rounded-[1.5rem] overflow-hidden bg-slate-100 aspect-[9/16]" data-id="element-954" data-mp="py0lsr596mu5">
                    <iframe src="https://www.instagram.com/reel/DWMvJXWgcw9/embed" title="Vidéo de présentation du dispositif CAP 360" className="absolute inset-0 w-full h-full" frameBorder="0" scrolling="no" allowTransparency={true} data-id="element-955" data-mp="2rr7xzrpkkobt"></iframe>
                  </div>
                </div>
                <p className="text-center text-sm text-muted-foreground mt-5 max-w-sm mx-auto leading-relaxed" data-id="element-956" data-mp="cxk3xcrafajt">
                  <span className="block font-bold text-primary-dark mb-1" data-id="element-957" data-mp="7wmlr06a2gib">
                    CAP 360 présenté par notre équipe
                  </span>
                  Le dispositif, son déroulé et ce qu'il apporte concrètement
                  aux futurs apprenants.
                </p>
              </div>
            </div>
          </div>

          {/* Les 3 piliers */}
          <div className="mt-20 grid sm:grid-cols-3 gap-6" data-id="element-958" data-mp="rl5879zwfftb">
            <Card className="border-none shadow-xl hover:-translate-y-2 transition-transform duration-300 group bg-white" data-id="element-959" data-mp="rnrhbe2fu3dn">
              <div className="h-2 w-full bg-primary" data-id="element-960" data-mp="10mlilhqprhxj" />
              <CardContent className="p-8" data-id="element-961" data-mp="10nwiv7kya7jw">
                <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300" data-id="element-962" data-mp="1k7c0s3ehbsev">
                  <Target className="w-8 h-8 text-primary" data-id="element-963" data-mp="1p4vl9l1rt1f9" />
                </div>
                <h3 className="font-extrabold font-heading text-xl text-primary-dark mb-1" data-id="element-964" data-mp="1a9fp9ahmqvg8">
                  CLARIFIER
                </h3>
                <p className="text-muted-foreground" data-id="element-965" data-mp="11hnjve7gqcii">
                  Définir un projet clair et réaliste
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:-translate-y-2 transition-transform duration-300 group bg-white" data-id="element-966" data-mp="16a6tk0314ni1">
              <div className="h-2 w-full bg-brand-orange" data-id="element-967" data-mp="wi15v2jt8s8n" />
              <CardContent className="p-8" data-id="element-968" data-mp="wikbbjs9xo5s">
                <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300" data-id="element-969" data-mp="ihztapu6nwdc">
                  <Briefcase className="w-8 h-8 text-brand-orange" data-id="element-970" data-mp="mm7jkrgahkdo" />
                </div>
                <h3 className="font-extrabold font-heading text-xl text-primary-dark mb-1" data-id="element-971" data-mp="fg522b1jq9fa">
                  STRUCTURER
                </h3>
                <p className="text-muted-foreground" data-id="element-972" data-mp="5qtyt51cbyzb">
                  Méthode, outils et cadre pour avancer
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:-translate-y-2 transition-transform duration-300 group bg-white" data-id="element-973" data-mp="q670h5afe41">
              <div className="h-2 w-full bg-primary-dark" data-id="element-974" data-mp="3nwzz39vnwzlg" />
              <CardContent className="p-8" data-id="element-975" data-mp="3nyt4ymygbfse">
                <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300" data-id="element-976" data-mp="3ix3sd2vkyu27">
                  <Users className="w-8 h-8 text-primary-dark" data-id="element-977" data-mp="3e0n1i0wzy1lf" />
                </div>
                <h3 className="font-extrabold font-heading text-xl text-primary-dark mb-1" data-id="element-978" data-mp="1h5h6tgugjrd4">
                  ACTIVER
                </h3>
                <p className="text-muted-foreground" data-id="element-979" data-mp="1kofzb2dmvf8j">
                  Passer à l'action face aux recruteurs
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Partenaires du dispositif */}
          <div className="mt-16 bg-muted rounded-3xl p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8" data-id="element-980" data-mp="1tht3j0h5e39x">
            <div className="md:flex-1" data-id="element-981" data-mp="1yf2x8sbkl13k">
              <h4 className="font-extrabold font-heading text-xl text-primary-dark mb-2" data-id="element-982" data-mp="11pllpwpwokty">
                Un projet porté par Zillenium Alternance
              </h4>
              <p className="text-muted-foreground leading-relaxed" data-id="element-983" data-mp="15r73nwharpb0">
                CAP 360 est soutenu par la{' '}
                <strong className="text-primary-dark" data-id="element-984" data-mp="1asd0a5e2vwpt">Ville de Joigny</strong>{' '}
                et <strong className="text-primary-dark" data-id="element-985" data-mp="1fn0enbfdbaqo">Domanys</strong>, dans
                le cadre du{' '}
                <strong className="text-primary-dark" data-id="element-986" data-mp="2m7ji1amoz76k">contrat de ville</strong>.
              </p>
            </div>
            <img src="https://cdn.magicpatterns.com/uploads/1cWwhiTx9jYMm4EBtCYEgp/PHOTO-2026-08-06-20-46-14.jpg" alt="Logos des partenaires du dispositif CAP 360 : Quartiers 2030, Domanys et Ville de Joigny" className="w-full md:w-auto md:max-w-sm rounded-2xl bg-white p-5 shrink-0" data-id="element-987" data-mp="21g4lp9bnn2if" />
          </div>
        </div>
      </section>

      {/* Contact direct */}
      <section className="py-24 bg-white" data-id="element-988" data-mp="1ip3l6guvyywr">
        <div className="container max-w-6xl mx-auto" data-id="element-989" data-mp="1iq4v2i061fcw">
          <ContactCTA title="Envie d'en savoir plus rapidement ? Appelez-nous" subtitle="Un conseiller répond à vos questions sur les formations, l'admission et la recherche d'entreprise. Vous pouvez aussi nous écrire sur WhatsApp, c'est souvent le plus rapide." variant="light" data-id="element-990" data-mp="1dr239z9755mv" />
        </div>
      </section>

      <div id="candidat-form" className="py-24 bg-muted scroll-mt-24" data-id="element-991" data-mp="zx110vj1d0hr">
        <div className="container max-w-4xl mx-auto" data-id="element-992" data-mp="uz9941ie5edk">
          <div className="bg-white border-none rounded-3xl p-8 md:p-16 text-center shadow-2xl relative overflow-hidden" data-id="element-993" data-mp="36ne21fk1orm1">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-orange rounded-full blur-[80px] opacity-10 pointer-events-none" data-id="element-994" data-mp="3bee04o4whfq4" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary rounded-full blur-[80px] opacity-10 pointer-events-none" data-id="element-995" data-mp="2wqsw8s07bch5" />

            <div className="relative z-10" data-id="element-996" data-mp="3pdvt7fp8bf5a">
              <h2 className="text-4xl font-extrabold font-heading text-primary-dark mb-6" data-id="element-997" data-mp="3pf6y5iueusxz">
                Prêt à vous lancer ?
              </h2>
              <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-998" data-mp="3uam6jv9nfwn0">
                "Bienvenue ! Ce formulaire rapide nous permet de mieux vous
                connaître et de comprendre votre projet professionnel..."
              </p>
              <div className="mt-8" data-id="element-999" data-mp="24wh70wbuqcw5">
                <CandidatForm data-id="element-1000" data-mp="28znkop1an6cn" />
              </div>
              <div className="mt-12 pt-8 border-t border-slate-100" data-id="element-1001" data-mp="290f7bacwxtqs">
                <Link to="/entreprises" className="text-base font-medium text-primary hover:text-brand-orange transition-colors underline underline-offset-4" data-id="element-1002" data-mp="30nqbms7pg79k">
                  Vous êtes une entreprise ? Recruter un alternant
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}